import { AngleDown, Bars, CheckCircle, Close, UserAdd } from 'flowbite-react-icons/outline'
import { useEffect, useMemo, useRef, useState } from 'react'
import { lockBodyScroll, unlockBodyScroll } from '@/components/common/bodyScrollLock'

/**
 * Narrow-screen (`< lg`) trigger that opens the order navigation drawer. It is
 * owned conceptually by {@link OrderSideNav} but rendered separately — inline in
 * the page header next to the patient name — so the burger reads as part of the
 * header rather than an orphaned control above the content row. The drawer and
 * its backdrop still live in {@link OrderSideNav}; this button only flips the
 * (lifted) open state. Hidden at `≥ lg` where the inline sticky nav is shown.
 */
export function OrderNavToggle({
  drawerOpen,
  onOpen,
  className = '',
}: {
  drawerOpen: boolean
  onOpen: () => void
  className?: string
}) {
  return (
    <button
      type="button"
      onClick={onOpen}
      aria-label="Open order navigation"
      aria-expanded={drawerOpen}
      className={`inline-flex h-10 w-10 shrink-0 cursor-pointer items-center justify-center rounded-full border border-border-base bg-white text-text-body shadow-xs transition-colors hover:bg-background-secondary-medium hover:text-text-heading focus:outline-none focus:ring focus:ring-brand-600/20 lg:hidden dark:border-gray-700 dark:bg-gray-900 dark:text-gray-400 dark:hover:bg-gray-800 dark:hover:text-white ${className}`}
    >
      <Bars className="h-5 w-5" aria-hidden />
    </button>
  )
}

export interface OrderSideNavSection {
  /** DOM id of the section anchor this item scrolls to. */
  id: string
  label: string
  /**
   * When true the item shows a brand-colored left indicator bar (R5 "required
   * section"); otherwise a subtle gray bar marks it optional. The brand bar is
   * dropped once the section is `completed` (see below), so purple only flags
   * outstanding required work.
   */
  required?: boolean
  /**
   * When true the required section's brand indicator bar is suppressed (the
   * subtle gray bar is shown instead) because the section's data is complete.
   * Ignored for optional sections, which never show the brand bar.
   */
  completed?: boolean
}

/**
 * R5 (side-nav comparator detail): one collapsible nav group per saved
 * comparator. In the stacked layout every comparator's cards are always
 * mounted in the second column; selecting the group (or any of its three
 * sub-sections) scrolls to that comparator's cards rather than swapping views.
 */
export interface OrderSideNavComparatorGroup {
  /** Stable comparator id; matches `selectedComparatorId`. */
  id: string
  /** Relationship label (e.g. "Mother"). */
  label: string
  /** Demographics / Clinical History / Consent anchors. */
  subSections: OrderSideNavSection[]
  /**
   * Whether every required sub-section is complete. When false, the group
   * header shows the brand (purple) accent so a collapsed comparator still
   * signals it has outstanding work — the header is the aggregate of its
   * sub-sections. Defaults to complete when omitted.
   */
  completed?: boolean
}

interface OrderSideNavProps {
  sections: OrderSideNavSection[]
  /** Collapsible group heading (R5: "Patient"). */
  groupLabel?: string
  /** When provided, renders the "Add Comparator" action at the bottom. */
  onAddComparator?: () => void
  /**
   * When true, the "Add Comparator" action is rendered but disabled (e.g. the
   * per-order comparator cap has been reached). Ignored unless
   * `onAddComparator` is provided.
   */
  addComparatorDisabled?: boolean
  /** Tooltip/title explaining why "Add Comparator" is disabled. */
  addComparatorDisabledReason?: string
  /** R5: one nav group per saved comparator (Demographics / Clinical History / Consent). */
  comparatorGroups?: OrderSideNavComparatorGroup[]
  /** R5: currently-active comparator id (drives the group-header highlight); `null` = Patient. */
  selectedComparatorId?: string | null
  /** R5: report the active comparator (or `null` for patient) as the user scrolls/clicks. */
  onSelectComparator?: (id: string | null) => void
  /**
   * Lifted narrow-screen drawer visibility. When provided (together with
   * {@link onDrawerOpenChange}) the open/close state is controlled by the
   * parent so the {@link OrderNavToggle} burger can live in the page header
   * while the drawer + backdrop stay here. When omitted the nav keeps its own
   * internal state and renders its own burger (legacy/standalone usage).
   */
  drawerOpen?: boolean
  /** Setter paired with {@link drawerOpen} to lift the drawer open state. */
  onDrawerOpenChange?: (open: boolean) => void
}

/**
 * R5 "Order Start (side nav)" left navigation. Presentation-only: it scrolls
 * the page to section anchors (by DOM id) and reflects the section currently in
 * view. It owns no order state. Rendered only when ``featureFlags.orderSideNav``
 * is enabled; the default OrderStart layout is single-column.
 */
export function OrderSideNav({
  sections,
  groupLabel = 'Patient',
  onAddComparator,
  addComparatorDisabled = false,
  addComparatorDisabledReason,
  comparatorGroups = [],
  selectedComparatorId = null,
  onSelectComparator,
  drawerOpen: drawerOpenProp,
  onDrawerOpenChange,
}: OrderSideNavProps) {
  const [open, setOpen] = useState(true)
  const [activeId, setActiveId] = useState<string | null>(sections[0]?.id ?? null)
  const [collapsedGroupIds, setCollapsedGroupIds] = useState<Set<string>>(new Set())

  // Narrow-screen (`< lg`) drawer visibility. At `≥ lg` the nav is an inline
  // sticky column and this flag is irrelevant (the responsive classes force it
  // visible via `lg:` prefixes), so a resize can never leave a stuck overlay.
  //
  // The open state is controlled when the parent supplies `drawerOpen` +
  // `onDrawerOpenChange` (so the burger can render in the page header); it
  // otherwise falls back to local state. `setDrawerOpen` routes to whichever is
  // active so every call site (Escape, backdrop, nav selection) behaves the same.
  const [internalDrawerOpen, setInternalDrawerOpen] = useState(false)
  const isControlled = drawerOpenProp !== undefined && onDrawerOpenChange !== undefined
  const drawerOpen = isControlled ? drawerOpenProp : internalDrawerOpen
  const setDrawerOpen = (next: boolean) => {
    if (isControlled) onDrawerOpenChange(next)
    else setInternalDrawerOpen(next)
  }

  // Attach the Escape listener only while the drawer is open, and remove it on
  // close and on unmount — no dangling listeners.
  useEffect(() => {
    if (!drawerOpen) return
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setDrawerOpen(false)
    }
    document.addEventListener('keydown', onKeyDown)
    return () => document.removeEventListener('keydown', onKeyDown)
  }, [drawerOpen])

  // Lock background scroll while the drawer is open (`< lg` only — the burger
  // that sets drawerOpen is `lg:hidden`). Uses the shared ref-counted <html>
  // overflow lock (from Modal) so the drawer and any open Modal coexist without
  // one's teardown clearing a lock the other still needs, and so the page keeps
  // its scroll position in place (no jump/flicker).
  useEffect(() => {
    if (!drawerOpen) return
    lockBodyScroll()
    return () => {
      unlockBodyScroll()
    }
  }, [drawerOpen])

  // Every anchor mounted in the second column. In the stacked layout the
  // patient sections AND every comparator's sub-sections are always mounted at
  // once, so scroll-spy observes them all and the active highlight tracks
  // whichever section is in view as the user scrolls the single column.
  const activeAnchorIds = useMemo(() => {
    const comparatorAnchors = comparatorGroups.flatMap((g) => g.subSections.map((s) => s.id))
    return [...sections.map((s) => s.id), ...comparatorAnchors]
  }, [comparatorGroups, sections])

  // Map each comparator sub-section anchor back to its owning comparator id so
  // scroll-spy can also light up the correct comparator group header as its
  // cards scroll into view.
  const anchorToComparatorId = useMemo(() => {
    const map = new Map<string, string>()
    for (const g of comparatorGroups) {
      for (const s of g.subSections) map.set(s.id, g.id)
    }
    return map
  }, [comparatorGroups])

  useEffect(() => {
    if (activeAnchorIds.length === 0) return
    if (typeof window === 'undefined') return
    const getEls = () =>
      activeAnchorIds
        .map((id) => document.getElementById(id))
        .filter((el): el is HTMLElement => el !== null)
    if (getEls().length === 0) return

    // Active-section detection by scroll position: the active anchor is the LAST
    // one whose top has crossed the trigger line (just below the sticky page
    // header), i.e. the section currently occupying the top of the viewport. A
    // wide IntersectionObserver band alone can't do this — with several sections
    // visible at once it would keep the topmost one highlighted even after the
    // user scrolled past it. We measure real positions so the highlight tracks
    // exactly the card in view.
    const measureActive = () => {
      // Prefer the shared sticky-top var (real header height + gap) that also
      // positions the pinned nav / person headers, so the highlight flips
      // exactly at the pin line. Add a little extra so a section counts as
      // active once its card content (not just its top edge) clears the pinned
      // person header. Fall back to measuring the header directly.
      const rootStyle = getComputedStyle(document.documentElement)
      const varRaw = rootStyle.getPropertyValue('--order-sticky-top').trim()
      const varPx = varRaw.endsWith('px') ? parseFloat(varRaw) : NaN
      const header = document.querySelector<HTMLElement>('[data-sticky-page-header]')
      const base = Number.isFinite(varPx) ? varPx : (header?.getBoundingClientRect().height ?? 0) + 16
      const triggerY = base + 48
      let current: string | null = null
      for (const id of activeAnchorIds) {
        const el = document.getElementById(id)
        if (!el) continue
        const top = el.getBoundingClientRect().top
        if (top - triggerY <= 1) {
          // This section's top is at/above the trigger line — it's a candidate;
          // keep taking later ones so we end on the lowest that still qualifies.
          current = id
        } else {
          // First section still below the trigger line — stop; earlier `current`
          // is the one in view.
          break
        }
      }
      // Before the first section reaches the trigger (top of page), fall back to
      // the first anchor so something is always highlighted.
      const nextId = current ?? activeAnchorIds[0] ?? null
      if (nextId) {
        setActiveId(nextId)
        onSelectComparator?.(anchorToComparatorId.get(nextId) ?? null)
      }
    }

    let frame = 0
    const onScroll = () => {
      if (frame) return
      frame = window.requestAnimationFrame(() => {
        frame = 0
        measureActive()
      })
    }

    measureActive()
    window.addEventListener('scroll', onScroll, { passive: true })
    window.addEventListener('resize', onScroll)
    return () => {
      if (frame) window.cancelAnimationFrame(frame)
      window.removeEventListener('scroll', onScroll)
      window.removeEventListener('resize', onScroll)
    }
  }, [activeAnchorIds, anchorToComparatorId, onSelectComparator])

  const lastAnchorsKey = useRef('')
  useEffect(() => {
    const key = activeAnchorIds.join('|')
    if (key !== lastAnchorsKey.current) {
      lastAnchorsKey.current = key
      if (!activeAnchorIds.includes(activeId ?? '')) {
        setActiveId(activeAnchorIds[0] ?? null)
      }
    }
  }, [activeAnchorIds, activeId])

  const scrollToAnchor = (id: string) => {
    const el = document.getElementById(id)
    if (el) {
      // Guard: some environments (e.g. jsdom in tests) don't implement
      // scrollIntoView. Still update the active highlight regardless.
      el.scrollIntoView?.({ behavior: 'smooth', block: 'start' })
      setActiveId(id)
    }
  }

  // Stacked layout: everything is mounted at once, so navigating is purely a
  // scroll. We still set the active comparator (via `scrollToAnchor` →
  // `setActiveId`, and the group-aware highlight below) so the nav reflects the
  // destination immediately without waiting for the scroll-spy observer.
  const selectAndScroll = (comparatorId: string | null, anchorId: string) => {
    onSelectComparator?.(comparatorId)
    scrollToAnchor(anchorId)
  }

  // Selecting a nav item must navigate AND dismiss the narrow-screen drawer so
  // it doesn't cover the content the user just jumped to. On `≥ lg` there is no
  // drawer, so closing it is a harmless no-op.
  const selectAndClose = (comparatorId: string | null, anchorId: string) => {
    selectAndScroll(comparatorId, anchorId)
    setDrawerOpen(false)
  }

  const toggleGroup = (id: string) => {
    setCollapsedGroupIds((prev) => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }

  const patientActive = selectedComparatorId == null

  // `< lg`: the nav is a fixed drawer, hidden until `drawerOpen`. It starts BELOW
  // the sticky page header (top-(--order-sticky-top), the same offset the desktop
  // sticky nav wrapper uses) and extends to the viewport bottom — otherwise, at
  // top:0 its first items (close button + Patient group) render behind the opaque
  // header and get clipped. `≥ lg`: it is always the inline sticky column (the
  // `lg:` classes override the mobile-open positioning), so a resize from `< lg`
  // to `≥ lg` can never leave the drawer stuck as an overlay.
  const navMobileClasses = drawerOpen
    ? 'fixed left-0 top-(--order-sticky-top) bottom-0 z-50 flex max-w-[85vw]'
    : 'hidden'
  const navClassName = [
    navMobileClasses,
    'w-[296px] shrink-0 flex-col gap-3 rounded-base border border-border-base bg-white p-3 shadow-xs',
    'dark:border-gray-700 dark:bg-gray-900',
    // Desktop: a plain inline column. The sticky pinning + top offset + internal
    // scroll are owned by the sticky WRAPPER in the page (so stickiness doesn't
    // depend on this element being a flex item). Here we just make it visible
    // and let it size to content; the wrapper caps height and scrolls if tall.
    'lg:flex lg:h-fit lg:max-w-none',
  ].join(' ')

  return (
    <>
      {/*
        Narrow-screen trigger; hidden at `≥ lg` where the inline nav is shown.
        Only rendered here when the drawer state is NOT lifted — a controlled
        parent renders {@link OrderNavToggle} itself (in the page header) so the
        burger sits inline next to the patient name instead of orphaned above the
        content row.
      */}
      {!isControlled && (
        <OrderNavToggle drawerOpen={drawerOpen} onOpen={() => setDrawerOpen(true)} />
      )}

      {/* Backdrop; only rendered while open and only at `< lg`. */}
      {drawerOpen && (
        <div
          data-testid="order-sidenav-backdrop"
          onClick={() => setDrawerOpen(false)}
          aria-hidden="true"
          className="fixed inset-0 z-40 bg-black/50 lg:hidden dark:bg-black/70"
        />
      )}

      <nav aria-label="Order sections" className={navClassName}>
        {/*
          Inner scroll region: holds all nav content at natural height and
          scrolls on its own at `< lg`, so the Add Comparator footer below stays
          pinned and visible for short content and reachable for tall content.
          At `≥ lg` the `lg:`-gated utilities neutralize the flex/scroll setup
          (and the nav is `lg:h-fit`), so this wrapper is an inert block that
          stacks its children exactly as before.
        */}
        <div className="flex min-h-0 flex-1 flex-col gap-3 overflow-y-auto lg:min-h-0 lg:flex-none lg:overflow-visible">
          {/* In-panel close control; only rendered while the drawer is open (`< lg`). */}
        {drawerOpen && (
          <div className="flex justify-end lg:hidden">
            <button
              type="button"
              onClick={() => setDrawerOpen(false)}
              aria-label="Close order navigation"
              className="inline-flex h-10 w-10 cursor-pointer items-center justify-center rounded-full text-text-body transition-colors hover:bg-background-secondary-medium hover:text-text-heading focus:outline-none focus:ring focus:ring-brand-600/20 dark:text-gray-400 dark:hover:bg-gray-800 dark:hover:text-white"
            >
              <Close className="h-5 w-5" aria-hidden />
            </button>
          </div>
        )}

      {/*
        UX-076: the Patient header splits into two distinct controls, matching
        the comparator-group headers below. The label navigates to the first
        patient section; a dedicated caret toggles collapse. Clicking the label
        does NOT auto-expand a collapsed group (parity with comparator labels).
      */}
      <div className="flex w-full items-center justify-between gap-2 rounded-base bg-background-secondary-medium px-2 py-1.5 transition-colors hover:bg-background-secondary-medium/80 dark:bg-gray-800 dark:hover:bg-gray-700">
        <button
          type="button"
          onClick={() => {
            const firstId = sections[0]?.id
            if (firstId) selectAndClose(null, firstId)
          }}
          aria-current={patientActive ? 'true' : undefined}
          className="flex flex-1 cursor-pointer items-center text-left"
        >
          <span className="border-l-2 border-brand-600 pl-2 text-base font-medium leading-6 text-text-fg-brand">
            {groupLabel}
          </span>
        </button>
        <button
          type="button"
          onClick={() => setOpen((value) => !value)}
          aria-expanded={open}
          aria-label={open ? `Collapse ${groupLabel}` : `Expand ${groupLabel}`}
          className="shrink-0 cursor-pointer rounded-base p-1.5 text-text-body transition-colors hover:bg-background-secondary-medium hover:text-text-heading dark:text-gray-400 dark:hover:bg-gray-700 dark:hover:text-white"
        >
          <AngleDown
            aria-hidden="true"
            className={`h-4 w-4 transition-transform ${open ? '' : '-rotate-90'}`}
          />
        </button>
      </div>

      {open && sections.length > 0 && (
        <ul className="flex flex-col gap-3">
          {sections.map((section) => {
            const isActive = patientActive && section.id === activeId
            return (
              <li key={section.id}>
                <button
                  type="button"
                  onClick={() => selectAndClose(null, section.id)}
                  aria-current={isActive ? 'true' : undefined}
                  className={`flex w-full cursor-pointer items-stretch gap-2 rounded-base py-1.5 pl-9 pr-2 text-left text-base font-medium leading-6 transition-colors hover:bg-background-secondary-medium dark:hover:bg-gray-800 ${
                    isActive
                      ? 'text-text-fg-brand'
                      : 'text-text-body hover:text-text-heading dark:text-gray-400 dark:hover:text-white'
                  }`}
                >
                  <span
                    aria-hidden="true"
                    className={`w-0.5 shrink-0 self-stretch rounded-full ${
                      section.required && !section.completed ? 'bg-brand-600' : 'bg-transparent'
                    }`}
                  />
                  <span className="flex-1">
                    {section.label}
                    {section.required && <span className="sr-only"> (required)</span>}
                  </span>
                  {section.completed === true && (
                    <>
                      <CheckCircle
                        aria-hidden="true"
                        className="h-4 w-4 shrink-0 self-center text-success-600 dark:text-success-500"
                      />
                      <span className="sr-only"> (completed)</span>
                    </>
                  )}
                </button>
              </li>
            )
          })}
        </ul>
      )}

      {comparatorGroups.map((group) => {
        const groupSelected = selectedComparatorId === group.id
        const collapsed = collapsedGroupIds.has(group.id)
        // The header aggregates the comparator's sub-sections: show the brand
        // accent while any required sub-section is outstanding, so a collapsed
        // comparator still flags incomplete work. A selected group keeps the
        // brand text/border for the active affordance regardless.
        const groupIncomplete = group.completed === false
        return (
          <div key={group.id} className="flex flex-col gap-3">
            <div
              className={`flex w-full items-center justify-between gap-2 rounded-base px-2 py-1.5 transition-colors ${
                groupSelected
                  ? 'bg-background-secondary-medium dark:bg-gray-800'
                  : 'hover:bg-background-secondary-medium dark:hover:bg-gray-800'
              }`}
            >
              <button
                type="button"
                onClick={() => {
                  // Stacked layout: the group header scrolls to the comparator's
                  // first sub-section (Demographics) rather than swapping views.
                  const firstId = group.subSections[0]?.id
                  if (collapsed) toggleGroup(group.id)
                  if (firstId) selectAndClose(group.id, firstId)
                  else {
                    onSelectComparator?.(group.id)
                    setDrawerOpen(false)
                  }
                }}
                aria-current={groupSelected ? 'true' : undefined}
                className="flex flex-1 cursor-pointer items-center text-left"
              >
                <span
                  className={`border-l-2 pl-2 text-base font-medium leading-6 ${
                    groupSelected
                      ? 'border-brand-600 text-text-fg-brand'
                      : groupIncomplete
                        ? 'border-brand-600 text-text-body dark:text-gray-300'
                        : 'border-text-body-subtle text-text-body dark:text-gray-300'
                  }`}
                >
                  {group.label}
                  {groupIncomplete && <span className="sr-only"> (incomplete)</span>}
                </span>
              </button>
              {group.completed === true && (
                <>
                  <CheckCircle
                    aria-hidden="true"
                    className="h-4 w-4 shrink-0 text-success-600 dark:text-success-500"
                  />
                  <span className="sr-only"> (completed)</span>
                </>
              )}
              <button
                type="button"
                onClick={() => toggleGroup(group.id)}
                aria-expanded={!collapsed}
                aria-label={`${collapsed ? 'Expand' : 'Collapse'} ${group.label}`}
                className="shrink-0 cursor-pointer rounded-base p-1.5 text-text-body transition-colors hover:bg-background-secondary-medium hover:text-text-heading dark:text-gray-400 dark:hover:bg-gray-700 dark:hover:text-white"
              >
                <AngleDown
                  aria-hidden="true"
                  className={`h-4 w-4 transition-transform ${collapsed ? '-rotate-90' : ''}`}
                />
              </button>
            </div>

            {!collapsed && (
              <ul className="flex flex-col gap-3">
                {group.subSections.map((sub) => {
                  const isActive = groupSelected && sub.id === activeId
                  return (
                    <li key={sub.id}>
                      <button
                        type="button"
                        onClick={() => selectAndClose(group.id, sub.id)}
                        aria-current={isActive ? 'true' : undefined}
                        className={`flex w-full cursor-pointer items-stretch gap-2 rounded-base py-1.5 pl-9 pr-2 text-left text-base font-medium leading-6 transition-colors hover:bg-background-secondary-medium dark:hover:bg-gray-800 ${
                          isActive
                            ? 'text-text-fg-brand'
                            : 'text-text-body hover:text-text-heading dark:text-gray-400 dark:hover:text-white'
                        }`}
                      >
                        <span
                          aria-hidden="true"
                          className={`w-0.5 shrink-0 self-stretch rounded-full ${
                            sub.required && !sub.completed ? 'bg-brand-600' : 'bg-transparent'
                          }`}
                        />
                        <span className="flex-1">{sub.label}</span>
                        {sub.completed === true && (
                          <>
                            <CheckCircle
                              aria-hidden="true"
                              className="h-4 w-4 shrink-0 self-center text-success-600 dark:text-success-500"
                            />
                            <span className="sr-only"> (completed)</span>
                          </>
                        )}
                      </button>
                    </li>
                  )
                })}
              </ul>
            )}
          </div>
        )
      })}
      </div>

      {onAddComparator && (
        <button
          type="button"
          disabled={addComparatorDisabled}
          title={addComparatorDisabled ? addComparatorDisabledReason : undefined}
          aria-disabled={addComparatorDisabled}
          onClick={() => {
            if (addComparatorDisabled) return
            // Launching the Add Comparator flow navigates away from the nav
            // (a modal opens, then its new comparator is selected on save), so
            // dismiss the narrow-screen drawer too — parity with section and
            // group-header selection, which also close the drawer.
            onAddComparator()
            setDrawerOpen(false)
          }}
          className="mt-1 inline-flex w-full shrink-0 cursor-pointer items-center justify-center gap-2 rounded-full border border-border-brand bg-background-brand-softer px-4 py-2.5 text-sm font-medium text-text-fg-brand shadow-xs transition-colors hover:bg-brand-100 dark:hover:bg-gray-800 disabled:cursor-not-allowed disabled:border-border-base disabled:bg-white disabled:text-text-body disabled:opacity-50 disabled:hover:bg-white dark:disabled:border-gray-700 dark:disabled:bg-gray-900 dark:disabled:text-gray-300 dark:disabled:hover:bg-gray-900"
        >
          <UserAdd className="h-4 w-4" />
          Add Comparator
        </button>
      )}
      </nav>
    </>
  )
}
